const crypto = require('crypto');

function hexToBuffer(hexString) {
  if (!hexString) {
    return Buffer.alloc(0);
  }

  let normalized = String(hexString).trim();
  if (normalized.startsWith('0x') || normalized.startsWith('0X')) {
    normalized = normalized.slice(2);
  }
  if (normalized.length % 2 === 1) {
    normalized = `0${normalized}`;
  }
  return Buffer.from(normalized, 'hex');
}

function concatByteArrays(...arrays) {
  const parts = [];

  for (const arr of arrays) {
    if (arr == null) {
      continue;
    }
    if (Buffer.isBuffer(arr) || arr instanceof Uint8Array) {
      parts.push(Buffer.from(arr));
      continue;
    }
    if (Array.isArray(arr)) {
      parts.push(Buffer.from(arr));
    }
  }

  return Buffer.concat(parts);
}

function sliceArray(array, length) {
  const values = Array.from(array);
  return length == null ? values : values.slice(0, length);
}

function hmacSha256Sign(key, data) {
  const keyBuffer = typeof key === 'string' ? Buffer.from(key, 'utf8') : Buffer.from(key);
  const dataBuffer = typeof data === 'string' ? Buffer.from(data, 'utf8') : Buffer.from(data);
  return crypto.createHmac('sha256', keyBuffer).update(dataBuffer).digest('hex');
}

function base64UrlToBuffer(value) {
  const normalized = String(value)
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(value.length / 4) * 4, '=');
  return Buffer.from(normalized, 'base64');
}

function getXCoordinateHexFromCertificate(certificatePem) {
  const certificate = new crypto.X509Certificate(certificatePem);
  const jwk = certificate.publicKey.export({ format: 'jwk' });
  return base64UrlToBuffer(jwk.x).toString('hex').padStart(64, '0');
}

function getPrivateScalarHex(privateKeyPem) {
  const privateKey = crypto.createPrivateKey(privateKeyPem);
  const jwk = privateKey.export({ format: 'jwk' });
  return base64UrlToBuffer(jwk.d).toString('hex').padStart(64, '0');
}

function getF(privateKeyHex, publicKeyHex) {
  const ecdh = crypto.createECDH('prime256v1');
  ecdh.setPrivateKey(hexToBuffer(privateKeyHex));
  return ecdh.computeSecret(hexToBuffer(publicKeyHex));
}

function ne(privateKeyPem, certificatePem) {
  const compressedPublicKeyHex = `02${getXCoordinateHexFromCertificate(certificatePem)}`;
  const privateKeyHex = getPrivateScalarHex(privateKeyPem);
  const sharedKey = getF(privateKeyHex, compressedPublicKeyHex);
  const zeroKey = Buffer.alloc(32);
  const emptyBytes = Buffer.alloc(0);
  const outputLength = 32;

  function kdfFunction(seedHex, extraBytes, requiredLength = 32) {
    let output = Buffer.alloc(0);
    let previousHex = '';
    let counter = 0;

    while (output.length < requiredLength) {
      counter += 1;
      const previousBytes = previousHex ? hexToBuffer(previousHex) : Buffer.alloc(0);
      const combined = concatByteArrays(
        [],
        sliceArray(previousBytes),
        sliceArray(extraBytes),
        [counter],
      );

      previousHex = hmacSha256Sign(seedHex ? hexToBuffer(seedHex) : Buffer.alloc(0), combined);
      output = Buffer.concat([output, hexToBuffer(previousHex)]);
    }

    return output.subarray(0, requiredLength);
  }

  const initialKey = hmacSha256Sign(zeroKey, sharedKey);
  return kdfFunction(initialKey, emptyBytes, outputLength);
}

function customHexBase64(hexString) {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  let result = '';

  for (let i = 0; i < hexString.length - (hexString.length % 3); i += 3) {
    const value = Number.parseInt(hexString.slice(i, i + 3), 16);
    result += alphabet[value >> 6] + alphabet[value & 63];
  }

  const remaining = hexString.length % 3;
  if (remaining === 1) {
    const value = Number.parseInt(hexString.slice(-1), 16);
    result += alphabet[value << 2];
  } else if (remaining === 2) {
    const value = Number.parseInt(hexString.slice(-2), 16);
    result += alphabet[value >> 2] + alphabet[(value & 3) << 4];
  }

  while (result.length % 4 !== 0) {
    result += '=';
  }

  return result;
}

function getHeadersBdTicketGuardClientData(
  ticket,
  path,
  timestamp,
  tsSign,
  privateKeyPem,
  certificatePem,
) {
  const secretKey = ne(privateKeyPem, certificatePem);
  const dataToSign = `ticket=${ticket}&path=${path}&timestamp=${timestamp}`;
  const signature = hmacSha256Sign(secretKey, dataToSign);
  const reqSign = customHexBase64(signature);

  const clientData = {
    ts_sign: tsSign,
    req_content: 'ticket,path,timestamp',
    req_sign: reqSign,
    timestamp,
  };

  return Buffer.from(JSON.stringify(clientData), 'utf8').toString('base64');
}

function getBdV2(responseObj, options = {}) {
  const timestamp = Math.trunc(Date.now() / 1000);
  const path = options.path ?? '/web/api/media/aweme/create_v2/';

  const headersBdTicketGuardClientData = getHeadersBdTicketGuardClientData(
    responseObj.ticket,
    path,
    timestamp,
    responseObj.ts_sign,
    responseObj.ec_privateKey,
    responseObj.certificate,
  );

  return {
    headers_bd_ticket_guard_client_data: headersBdTicketGuardClientData,
    headers_bd_ticket_guard_ree_public_key: responseObj.bd_ticket_guard_ree_public_key,
  };
}

module.exports = {
  hexToBuffer,
  concatByteArrays,
  sliceArray,
  customHexBase64,
  Lt: hexToBuffer,
  Kt: concatByteArrays,
  Rt: sliceArray,
  hmacSha256Sign,
  hmac_sha256_sign: hmacSha256Sign,
  getF,
  get_f: getF,
  ne,
  y: customHexBase64,
  getHeadersBdTicketGuardClientData,
  get_headers_bd_ticket_guard_client_data: getHeadersBdTicketGuardClientData,
  getBdV2,
  get_bd_v2: getBdV2,
};